import React, { useState, useEffect, useRef } from "react";
import api from "../../services/api";

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hello! I am your MizuWatch AI Assistant. How can I help you with your water utility metrics today?",
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const messagesEndRef = useRef(null);

  const [position, setPosition] = useState({ right: 24, bottom: 24 });
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const dragStartPos = useRef({ right: 24, bottom: 24 });

  const handleMouseDown = (e) => {
    isDragging.current = false;
    dragStart.current = { x: e.clientX, y: e.clientY };
    dragStartPos.current = { ...position };
    
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    const deltaX = e.clientX - dragStart.current.x;
    const deltaY = e.clientY - dragStart.current.y;
    
    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      isDragging.current = true;
    }
    
    setPosition({
      right: Math.max(10, Math.min(window.innerWidth - 70, dragStartPos.current.right - deltaX)),
      bottom: Math.max(10, Math.min(window.innerHeight - 70, dragStartPos.current.bottom - deltaY))
    });
  };

  const handleMouseUp = () => {
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  const handleTouchStart = (e) => {
    isDragging.current = false;
    const touch = e.touches[0];
    dragStart.current = { x: touch.clientX, y: touch.clientY };
    dragStartPos.current = { ...position };
    
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    const deltaX = touch.clientX - dragStart.current.x;
    const deltaY = touch.clientY - dragStart.current.y;
    
    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      isDragging.current = true;
    }
    
    setPosition({
      right: Math.max(10, Math.min(window.innerWidth - 70, dragStartPos.current.right - deltaX)),
      bottom: Math.max(10, Math.min(window.innerHeight - 70, dragStartPos.current.bottom - deltaY))
    });
  };

  const handleTouchEnd = () => {
    window.removeEventListener("touchmove", handleTouchMove);
    window.removeEventListener("touchend", handleTouchEnd);
  };

  const handleBtnClick = (e) => {
    if (isDragging.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    handleToggle();
  };

  // Auto-scroll to the bottom of the message container
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  // Fetch chat history on component mount
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const raw = localStorage.getItem("MCData");
        let token = "";
        if (raw) {
          const parsed = JSON.parse(raw);
          token = parsed?.token || "";
        }
        if (!token) return;

        const axios = (await import("axios")).default;
        const isLocalDev = import.meta.env.DEV;
        const baseUrl = isLocalDev ? "http://localhost:5000" : (import.meta.env.VITE_API_URL || "");
        const chatbotUrl = `${baseUrl.replace(/\/$/, "")}/apis/chatbot`;

        const response = await axios.get(chatbotUrl, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (response.data.status && response.data.messages && response.data.messages.length > 0) {
          // Filter to only display user and assistant messages to the user
          const displayableMessages = response.data.messages.filter(
            (msg) => (msg.role === "user" || msg.role === "assistant") && msg.content
          );

          if (displayableMessages.length > 0) {
            setMessages(displayableMessages);
          }
        }
      } catch (err) {
        console.error("Failed to load chat history:", err);
      }
    };

    fetchHistory();
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMessage = {
      role: "user",
      content: inputValue,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      // Retrieve token from MCData
      const raw = localStorage.getItem("MCData");
      let token = "";
      if (raw) {
        const parsed = JSON.parse(raw);
        token = parsed?.token || "";
      }

      // Import axios dynamically to avoid bundle size issues
      const axios = (await import("axios")).default;
      const isLocalDev = import.meta.env.DEV;
      const baseUrl = isLocalDev ? "http://localhost:5000" : (import.meta.env.VITE_API_URL || "");
      const chatbotUrl = `${baseUrl.replace(/\/$/, "")}/apis/chatbot`;
      
      const response = await axios.post(chatbotUrl, 
        { message: userMessage.content },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      const assistantMessage = {
        role: "assistant",
        content: response.data.response,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Chatbot request error:", error);
      const errorMessage = {
        role: "assistant",
        content: "Sorry, I encountered an error processing your request. Please try again.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to render message content with simple line breaks and basic bold parsing
  const formatMessage = (text) => {
    if (!text) return "";
    return text.split("\n").map((line, idx) => {
      // Basic bold formatting parser (**text** -> <strong>text</strong>)
      const parts = line.split(/\*\*([^*]+)\*\*/g);
      return (
        <div key={idx} style={{ marginBottom: "6px", lineHeight: "1.4" }}>
          {parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))}
        </div>
      );
    });
  };

  return (
    <>
      {/* Scope CSS inside a tag to prevent collision */}
      <style>{`
        .mizu-chat-btn {
          position: fixed;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%);
          box-shadow: 0 4px 15px rgba(139, 92, 246, 0.4);
          border: none;
          cursor: grab;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          touch-action: none;
        }
        .mizu-chat-btn:active {
          cursor: grabbing;
        }
        .mizu-chat-btn:hover {
          transform: scale(1.08);
          box-shadow: 0 6px 20px rgba(139, 92, 246, 0.6);
        }
        .mizu-chat-btn svg {
          width: 28px;
          height: 28px;
          color: white;
          transition: transform 0.3s ease;
        }
        .mizu-chat-btn.open svg {
          transform: rotate(90deg);
        }
        .mizu-chat-container {
          position: fixed;
          bottom: 96px;
          right: 24px;
          width: 370px;
          height: 530px;
          background: rgba(12, 10, 24, 0.96);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(6, 182, 212, 0.25);
          border-radius: 16px;
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);
          z-index: 9999;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          transform-origin: bottom right;
        }
        .mizu-chat-container.closed {
          opacity: 0;
          transform: scale(0.8) translate(20px, 20px);
          pointer-events: none;
        }
        .mizu-chat-header {
          padding: 16px;
          background: linear-gradient(135deg, #1e1b4b 0%, #312e81 100%);
          border-bottom: 1px solid rgba(255, 255, 255, 0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: grab;
          user-select: none;
        }
        .mizu-chat-header:active {
          cursor: grabbing;
        }
        .mizu-chat-title-group {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .mizu-chat-status {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #10b981;
          box-shadow: 0 0 8px #10b981;
        }
        .mizu-chat-title {
          font-weight: 600;
          color: white;
          font-size: 15px;
          margin: 0;
        }
        .mizu-chat-close {
          background: transparent;
          border: none;
          cursor: pointer;
          color: rgba(255, 255, 255, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
        }
        .mizu-chat-close:hover {
          color: white;
        }
        .mizu-chat-body {
          flex: 1;
          padding: 16px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        /* Custom scrollbar */
        .mizu-chat-body::-webkit-scrollbar {
          width: 6px;
        }
        .mizu-chat-body::-webkit-scrollbar-track {
          background: transparent;
        }
        .mizu-chat-body::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 4px;
        }
        .mizu-chat-body::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.4);
        }
        .mizu-msg-bubble {
          max-width: 80%;
          padding: 10px 14px;
          border-radius: 12px;
          font-size: 13.5px;
          word-wrap: break-word;
          color: #f1f5f9;
        }
        .mizu-chat-container.maximized {
          width: 50vw;
          height: 80vh;
          border: 1.5px solid rgba(139, 92, 246, 0.45);
          box-shadow: 0 16px 40px rgba(139, 92, 246, 0.15), 0 12px 30px rgba(0, 0, 0, 0.5);
          background: rgba(12, 16, 33, 0.96);
        }
        @media (max-width: 768px) {
          .mizu-chat-container.maximized {
            width: calc(100vw - 32px);
            height: calc(100vh - 140px);
            right: 16px;
            bottom: 96px;
          }
        }
        .mizu-chat-header-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .mizu-chat-maximize {
          background: transparent;
          border: none;
          cursor: pointer;
          color: rgba(255, 255, 255, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
          transition: color 0.2s;
        }
        .mizu-chat-maximize:hover {
          color: white;
        }
        .mizu-msg-bubble.assistant {
          align-self: flex-start;
          background: rgba(255, 255, 255, 0.08);
          border-top-left-radius: 2px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        .mizu-msg-bubble.user {
          align-self: flex-end;
          background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
          border-top-right-radius: 2px;
        }
        .mizu-msg-time {
          font-size: 10px;
          color: rgba(255, 255, 255, 0.4);
          margin-top: 4px;
          text-align: right;
        }
        .mizu-chat-footer {
          padding: 12px;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
          background: rgba(10, 8, 20, 0.5);
        }
        .mizu-chat-form {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .mizu-chat-input {
          flex: 1;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 24px;
          padding: 8px 16px;
          color: white;
          font-size: 13.5px;
          outline: none;
          transition: border-color 0.2s ease;
        }
        .mizu-chat-input:focus {
          border-color: #8b5cf6;
        }
        .mizu-chat-send {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #8b5cf6;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          transition: background-color 0.2s ease;
        }
        .mizu-chat-send:hover {
          background: #7c3aed;
        }
        .mizu-chat-send:disabled {
          background: rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.3);
          cursor: not-allowed;
        }
        /* Loading dots animation */
        .mizu-typing-dots {
          display: flex;
          gap: 4px;
          padding: 6px 0;
          align-items: center;
        }
        .mizu-dot {
          width: 6px;
          height: 6px;
          background: rgba(255, 255, 255, 0.5);
          border-radius: 50%;
          animation: mizuBlink 1.4s infinite both;
        }
        .mizu-dot:nth-child(2) { animation-delay: .2s; }
        .mizu-dot:nth-child(3) { animation-delay: .4s; }
        @keyframes mizuBlink {
          0% { opacity: .2; }
          20% { opacity: 1; }
          100% { opacity: .2; }
        }
      `}</style>

      {/* Floating Action Button */}
      <button 
        className={`mizu-chat-btn ${isOpen ? "open" : ""}`}
        onClick={handleBtnClick}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        style={{
          right: `${position.right}px`,
          bottom: `${position.bottom}px`
        }}
        aria-label="Toggle MizuWatch AI Assistant"
      >
        {isOpen ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            {/* Left Brain Hemisphere */}
            <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-2.5 2.5C6 22 3 19 3 12S6 2 9.5 2z" />
            {/* Right Hemisphere as neural nodes/circuits */}
            <path d="M12 4.5a2.5 2.5 0 0 1 2.5-2.5C18 2 21 5 21 12s-3 10-6.5 10a2.5 2.5 0 0 1-2.5-2.5" strokeDasharray="2 2" />
            <circle cx="16" cy="7" r="1.2" fill="currentColor" />
            <circle cx="18" cy="12" r="1.2" fill="currentColor" />
            <circle cx="15" cy="17" r="1.2" fill="currentColor" />
            <path d="M12 7h2.5M12 12h4.5M12 17h1.5" />
          </svg>
        )}
      </button>

      {/* Chat Window Container */}
      <div 
        className={`mizu-chat-container ${isOpen ? "" : "closed"} ${isMaximized ? "maximized" : ""}`}
        style={{
          right: `${position.right}px`,
          bottom: `${position.bottom + 72}px`
        }}
      >
        {/* Header */}
        <div 
          className="mizu-chat-header"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div className="mizu-chat-title-group">
            <div className="mizu-chat-status"></div>
            <h3 className="mizu-chat-title">MizuWatch AI Assistant</h3>
          </div>
          <div className="mizu-chat-header-actions" onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
            <button className="mizu-chat-maximize" onClick={(e) => { e.stopPropagation(); setIsMaximized(!isMaximized); }} title={isMaximized ? "Restore" : "Maximize"}>
              {isMaximized ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="4 14 10 14 10 20"></polyline>
                  <polyline points="20 10 14 10 14 4"></polyline>
                  <line x1="14" y1="10" x2="21" y2="3"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <polyline points="9 21 3 21 3 15"></polyline>
                  <line x1="21" y1="3" x2="14" y2="10"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
              )}
            </button>
            <button className="mizu-chat-close" onClick={handleToggle}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        {/* Messages Body */}
        <div className="mizu-chat-body">
          {messages.map((msg, index) => (
            <div key={index} className={`mizu-msg-bubble ${msg.role}`}>
              {formatMessage(msg.content)}
              <div className="mizu-msg-time">{msg.timestamp}</div>
            </div>
          ))}

          {/* Typing Indicator */}
          {isLoading && (
            <div className="mizu-msg-bubble assistant">
              <div className="mizu-typing-dots">
                <div className="mizu-dot"></div>
                <div className="mizu-dot"></div>
                <div className="mizu-dot"></div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Footer Input */}
        <div className="mizu-chat-footer">
          <form className="mizu-chat-form" onSubmit={handleSend}>
            <input
              type="text"
              className="mizu-chat-input"
              placeholder="Ask me a question..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              disabled={isLoading}
            />
            <button 
              type="submit" 
              className="mizu-chat-send"
              disabled={!inputValue.trim() || isLoading}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default ChatbotWidget;
